'use client';

import * as React from 'react';
import * as CheckboxPrimitive from '@radix-ui/react-checkbox';
import { Check, Minus } from 'lucide-react';
import { tv } from 'tailwind-variants';

import { Label } from '@/components/ui/label.component';

export type CheckboxContainer = 'tile' | 'text' | 'box';

export type CheckboxAlign = 'center' | 'start';

const checkboxStyles = tv({
  slots: {
    control: [
      'peer h-6 w-6 shrink-0 rounded-sm border border-gray-400 ring-offset-background focus-visible:outline-none',
      'focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      'data-[state=checked]:bg-green-500 data-[state=checked]:border-green-500',
      'data-[state=indeterminate]:bg-green-500 data-[state=indeterminate]:border-green-500',
      'data-[state=checked]:text-primary-foreground data-[state=indeterminate]:text-primary-foreground',
    ],
    wrapper: 'flex items-center justify-center rounded-lg',
    root: 'flex',
    label: 'text-base text-gray-700 cursor-pointer',
  },

  variants: {
    align: {
      center: { root: 'items-center' },
      start: { root: 'items-start' },
    },
    container: {
      tile: {
        wrapper: 'w-12 h-12 min-w-12 min-h-12 bg-green-50',
        root: 'gap-4',
      },
      text: {
        root: 'gap-3',
        label: 'mt-1',
      },
      box: {
        control: 'focus-visible:ring-0 focus-visible:ring-offset-0',
        root: [
          'gap-3 rounded-lg border border-gray-300 p-2 cursor-pointer',
          'has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-ring has-[:focus-visible]:ring-offset-2',
        ],
      },
    },
    selected: {
      true: { label: 'font-semibold' },
      false: { label: 'font-normal' },
    },
    disabled: {
      true: { root: 'cursor-not-allowed opacity-50', label: 'cursor-not-allowed' },
    },
  },

  compoundVariants: [
    {
      container: 'box',
      selected: true,
      class: {
        root: 'border-green-100 bg-green-50',
      },
    },
  ],

  defaultVariants: {
    align: 'center',
    container: 'tile',
    selected: false,
    disabled: false,
  },
});

type CheckedState = CheckboxPrimitive.CheckboxProps['checked'];

type CheckboxProps = React.ComponentProps<typeof CheckboxPrimitive.Root> & {
  htmlFor?: string;
  container?: CheckboxContainer;
  align?: CheckboxAlign;
};

function Checkbox({
  className,
  htmlFor,
  container = 'tile',
  align = 'center',
  children,
  id,
  checked,
  defaultChecked,
  onCheckedChange,
  disabled,
  ...props
}: CheckboxProps) {
  const generatedId = React.useId();
  const [uncontrolledChecked, setUncontrolledChecked] = React.useState<CheckedState>(defaultChecked ?? false);

  const state = checked ?? uncontrolledChecked;
  const isSelected = state === true || state === 'indeterminate';
  const isIndeterminate = state === 'indeterminate';

  const { control, wrapper, root, label } = checkboxStyles({
    align,
    container,
    selected: isSelected,
    disabled,
  });

  const checkboxId = id ?? htmlFor ?? generatedId;
  const hasExplicitId = id !== undefined || htmlFor !== undefined;
  const labelsByAria = !hasExplicitId && container !== 'box';
  const labelId = `${checkboxId}-label`;

  const handleCheckedChange = (next: NonNullable<CheckedState>): void => {
    if (checked === undefined) {
      setUncontrolledChecked(next);
    }

    onCheckedChange?.(next);
  };

  const checkboxControl = (
    <div className={wrapper()}>
      <CheckboxPrimitive.Root
        className={control({ className })}
        aria-labelledby={labelsByAria ? labelId : undefined}
        {...props}
        id={checkboxId}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        onCheckedChange={handleCheckedChange}
      >
        <CheckboxPrimitive.Indicator className="flex items-center justify-center text-current">
          {isIndeterminate ? <Minus className="h-4 w-4" /> : <Check className="h-4 w-4" />}
        </CheckboxPrimitive.Indicator>
      </CheckboxPrimitive.Root>
    </div>
  );

  if (container === 'box') {
    return (
      <Label htmlFor={checkboxId} className={root()}>
        {checkboxControl}
        <span className={label()}>{children}</span>
      </Label>
    );
  }

  return (
    <div className={root()}>
      {checkboxControl}

      <Label
        id={labelsByAria ? labelId : undefined}
        htmlFor={labelsByAria ? undefined : checkboxId}
        className={label()}
      >
        {children}
      </Label>
    </div>
  );
}

export { Checkbox };
